<template>
    <div v-if="notifications">
        <!-- Heading -->
        <div class="d-flex flex-column flex-lg-row align-items-lg-center justify-content-lg-between mb-5">
            <div>
                <h1 class="mb-3">
                    Notifications
                </h1>
                <p>All property and viewing notifications will appear here</p>
            </div>
            <div class="d-flex flex-column align-items-end">
                <router-link
                    :to="{ name: 'my-profile' }"
                    class="btn btn--primary mb-3"
                >
                    Change Preferences
                </router-link>
                <ul class="list-unstyled mb-0 mt-4 mt-lg-0 filter-menu d-none d-md-flex">
                    <li><span>Filter</span></li>
                    <li>
                        <a
                            :class="{ active : !activeFilter || activeFilter === 'all' }"
                            href="#"
                            @click.prevent="switchFilter('all')"
                        >
                            All
                        </a>
                    </li>
                    <li>
                        <a
                            :class="{ active : activeFilter === 'unread' }"
                            href="#"
                            @click.prevent="switchFilter('unread')"
                        >
                            Unread
                        </a>
                    </li>
                    <li>
                        <a
                            :class="{ active : activeFilter === 'read' }"
                            href="#"
                            @click.prevent="switchFilter('read')"
                        >
                            Read
                        </a>
                    </li>
                    <li>
                        <a
                            :class="{ active : activeFilter === 'viewing' }"
                            href="#"
                            @click.prevent="switchFilter('viewing')"
                        >
                            Viewing
                        </a>
                    </li>
                    <li>
                        <a
                            :class="{ active : activeFilter === 'account' }"
                            href="#"
                            @click.prevent="switchFilter('account')"
                        >
                            Account
                        </a>
                    </li>
                    <li>
                        <a
                            :class="{ active : activeFilter === 'property' }"
                            href="#"
                            @click.prevent="switchFilter('property')"
                        >
                            Property
                        </a>
                    </li>
                </ul>
            </div>
        </div>

        <div class="card">
            <!-- Messages Heading -->
            <div class="d-flex align-items-center justify-content-between px-4 py-5">
                <h3>Alerts</h3>
                <div
                    v-if="notifications.total >= 1"
                    class="d-flex align-items-center"
                >
                    <span class="mr-4">{{ notifications.from }}-{{ notifications.to }} of {{ notifications.total }}</span>
                    <span class="d-flex align-items-center">
                        <router-link
                            :to="{
                                name: 'notifications',
                                params: { page: (notifications.current_page - 1) > 1 ? notifications.current_page - 1 : null },
                                query: { type: activeFilter }
                            }"
                            :disabled="!prevButtonActive"
                            tag="button"
                            class="btn--inherit"
                        >
                            <SVGSprite
                                name="arrow-square-left"
                                class="arrow-icon"
                            />
                        </router-link>
                        <router-link
                            :to="{
                                name: 'notifications',
                                params: { page: notifications.current_page + 1 },
                                query: { type: activeFilter }
                            }"
                            :disabled="!nextButtonActive"
                            tag="button"
                            class="btn--inherit"
                        >
                            <SVGSprite
                                name="arrow-square-right"
                                class="arrow-icon"
                            />
                        </router-link>
                    </span>
                </div>
            </div>

            <!-- Message listing -->
            <div
                v-if="notifications.total >= 1"
                id="table-messages"
                :class="{ 'filter-unread': activeFilter === 'unread' }"
                class="table-flex table-flex--plain table-flex--cell-1-md table-flex--cell-3-md table-flex--cell-4-md"
            >
                <div
                    v-if="notifications.data && notifications.data.length"
                    class="table-flex--header"
                >
                    <div>Type</div>
                    <div>Description</div>
                    <div>Date</div>
                    <div />
                </div>
                <div
                    v-for="notification in notifications.data"
                    :key="notification.id"
                >
                    <div>
                        <span
                            :class="{
                                'label-type--sell': notification.property_type === 'sale',
                                'label-type--let': notification.property_type === 'rent'
                            }"
                            class="label-type"
                        >
                            {{ notification.label }}
                        </span>
                    </div>
                    <div>
                        <p
                            :class="{ 'text-bold': !notification.read_at }"
                            class="color-dark-grey mb-0"
                        >
                            {{ notification.content }}
                        </p>
                    </div>
                    <div>{{ notification.created_at | moment('L h:mm a') }}</div>
                    <div class="d-flex justify-content-end">
                        <button
                            class="btn btn--outline btn--nomin btn--sm"
                            @click="viewRelatedLink(notification)"
                        >
                            View
                        </button>
                        <button
                            v-if="!notification.read_at"
                            type="button"
                            class="remove-button"
                            @click="markNotificationAsRead(notification)"
                        >
                            <SVGSprite
                                name="close"
                                class="remove-icon"
                            />
                        </button>
                    </div>
                </div>
                <div v-if="!notifications.data || !notifications.data.length">
                    <p class="color-dark-grey text-bold mb-0 p-4">
                        You do not currently have any notifications.
                    </p>
                </div>
            </div>
            <div
                v-else
                class="px-4 pb-4"
            >
                <p>You have no notifications</p>
            </div>
        </div>
    </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';

export default {
    name: 'Notifications',
    data() {
        return {
            activeFilter: this.$route.query.type
        };
    },
    computed: {
        ...mapState('notifications', ['notifications']),
        ...mapGetters('notifications', ['getRelatedLink']),
        prevButtonActive() {
            return this.notifications.current_page > 1;
        },
        nextButtonActive() {
            return this.notifications.current_page > 0 && this.notifications.current_page < this.notifications.last_page;
        }
    },
    mounted() {
        this.getNotifications({
            page: this.$route.params.page || 1,
            filter: this.activeFilter
        });
    },
    methods: {
        switchFilter(item) {
            this.activeFilter = item;
            this.$router.push({
                name: 'notifications',
                query: {
                    type: this.activeFilter
                }
            });
        },
        ...mapActions('notifications', {
            getNotifications: 'getNotifications',
            markNotificationsAsRead: 'markAsRead'
        }),
        markNotificationAsRead(notification) {
            notification.read_at = true;
            this.markNotificationsAsRead({ notificationIds: [notification.id] });
        },
        viewRelatedLink(notification) {
            if (!notification.read_at) {
                this.markNotificationAsRead(notification);
            }
            this.$router.push(this.getRelatedLink(notification));
        }
    },
    beforeRouteUpdate(to, from, next) {
        this.getNotifications({
            page: to.params.page || 1,
            filter: this.activeFilter
        })
            .then(() => {
                next();
            });
    }
};
</script>

<style lang="scss">
@include media-breakpoint-up(lg) {
    .page-notifications {
        .table-flex--cell-4-md > div > div:nth-of-type(4) {
            flex: 0 0 15rem;
        }
    }
}

.remove-button {
    padding: 0 0 0 2rem;

    @include media-breakpoint-up(lg) {
        padding: 0 2rem;
    }

    .remove-icon {
        width: 1.25rem;
        height: 1.25rem;
        color: theme-color('mid-grey');

        &:hover {
            color: theme-color('primary');
        }
    }
}
</style>
